import React, { lazy, Suspense, useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "./TopBar";
import WorkPanel, { DEFAULT_WORKSPACE_NODES, type WorkspaceNode } from "./WorkPanel";
import ChatSpine from "./chat";
import StatusBar from "./StatusBar";
import InlineChatCompose from "./InlineChatCompose";
import WorkspaceComposer, { type WorkspaceComposerFeedback } from "./WorkspaceComposer";
import { useChatBridge } from "./useChatBridge";
import { LifeSelectionProvider } from "./life";
import { AgentPresenceProvider, AgentPresenceLayer } from "./presence";
import { requestContextualChatReply } from "./contextual-chat";
import type { SSEEvent } from "../hooks/useSSE";
import { emitUxTelemetry } from "../telemetry/uxTelemetry";
import { buildSettingsPath } from "./settingsCatalog";
import { saveLastActiveConversationId } from "./chat/persistence";
import {
  buildWorkspaceCollectionApp,
  resolveWorkspaceNodeDestination,
  type WorkspaceCollectionId,
} from "./workspaceCollections";
import { buildWorkspaceBridgeContext } from "./workspaceContext";
import {
  generateSessionId,
  listSavedConversations,
  resolveInitialSessionId,
} from "./workspaceSession";
import { mapBackendAppToAppData, handleAppSSEEvents } from "./workspaceAppHelpers";
import type { WorkspaceTourSurface } from "./onboarding/registry";
import {
  buildSelectedTargetFromWorkspaceNode,
  buildSelectedTargetProvenance,
  extractSelectedTargetFromElement,
  type SelectedTarget,
} from "./selectedTarget";
import { DEFAULT_AGENT_NAME } from "../../shared/consumer-language";
import AppContainer, { type AppAction, type AppData } from "./AppContainer";
import CommandPalette from "./CommandPalette";
import { buildPaletteCommands } from "./buildPaletteCommands";

const LazyCommsHub = lazy(() => import("./CommsHub"));
const LazyContextFiles = lazy(() => import("../pages/ContextFiles"));
const LazyLifeCanvas = lazy(() =>
  import("./life").then((module) => ({ default: module.LifeCanvas })),
);

interface WorkspaceShellProps {
  sseConnected: boolean;
  notificationCount: number;
  sseEvents: SSEEvent[];
  theme: "dark" | "light";
  onThemeToggle: () => void;
  needsOnboarding?: boolean;
  onboardingBlocker?: { reason: string; message: string } | null;
}

const MOBILE_BREAKPOINT = 768;
const CONTEXTUAL_COMPOSE_WIDTH = 360;
const CONTEXTUAL_COMPOSE_ESTIMATED_HEIGHT = 220;
const CONTEXTUAL_COMPOSE_MARGIN = 12;

interface ElementAnchorRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ContextualPaneResult {
  assistantReply?: string;
  acknowledgment?: string;
  closeAfterSend?: boolean;
}

function isEditableTarget(el: HTMLElement): boolean {
  if (el.isContentEditable) return true;
  return Boolean(el.closest("input, textarea, select, option, [contenteditable='true']"));
}

function isExpansionIntentTarget(el: HTMLElement): boolean {
  const expandableRoot = el.closest(
    [
      "[aria-expanded]",
      "[aria-controls]",
      "[data-accordion]",
      "[data-disclosure]",
      "[data-expandable]",
      "[data-collapsible]",
      '[data-state="open"]',
      '[data-state="closed"]',
      '[data-state="collapsed"]',
      '[data-state="expanded"]',
      "summary",
      "details",
    ].join(", "),
  );

  if (!expandableRoot) return false;
  if (expandableRoot.getAttribute("data-contextual-chat-expandable") === "false") return false;

  if (
    expandableRoot.matches(
      [
        "summary",
        "details",
        "[aria-expanded]",
        "[aria-controls]",
        "[data-accordion]",
        "[data-disclosure]",
        "[data-expandable]",
        "[data-collapsible]",
        '[data-state="open"]',
        '[data-state="closed"]',
        '[data-state="collapsed"]',
        '[data-state="expanded"]',
      ].join(", "),
    )
  ) {
    return true;
  }

  const className =
    typeof expandableRoot.className === "string" ? expandableRoot.className.toLowerCase() : "";
  return /(accordion|disclosure|collapse|expand|chevron|tree-toggle|toggle)/.test(className);
}

function buildElementSelector(el: HTMLElement): string {
  const nodeId = el.dataset.nodeId;
  if (nodeId) return `[data-node-id="${nodeId}"]`;

  const lifeItemId = el.dataset.lifeItemId;
  if (lifeItemId) return `[data-life-item-id="${lifeItemId}"]`;

  if (el.id) return `#${el.id}`;

  const role = el.getAttribute("role");
  if (role) {
    return `${el.tagName.toLowerCase()}[role="${role}"]`;
  }

  return el.tagName.toLowerCase();
}

function deriveElementLabel(el: HTMLElement): string {
  const sidebarLabel = el.querySelector(".work-panel__node-label")?.textContent?.trim();
  if (sidebarLabel) return sidebarLabel;

  const ariaLabel = el.getAttribute("aria-label")?.trim();
  if (ariaLabel) return ariaLabel;

  const title = el.getAttribute("title")?.trim();
  if (title) return title;

  const text = el.textContent?.replace(/\s+/g, " ").trim();
  if (text) return text.slice(0, 80);

  return el.tagName.toLowerCase();
}

function computeContextualComposeStyle(anchor: ElementAnchorRect): React.CSSProperties {
  if (typeof window === "undefined") {
    return {
      position: "fixed",
      right: 20,
      bottom: 20,
      width: `min(${CONTEXTUAL_COMPOSE_WIDTH}px, calc(100vw - 24px))`,
      zIndex: 70,
    };
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const width = Math.min(CONTEXTUAL_COMPOSE_WIDTH, viewportWidth - CONTEXTUAL_COMPOSE_MARGIN * 2);

  let left = anchor.left + anchor.width + CONTEXTUAL_COMPOSE_MARGIN;
  if (left + width > viewportWidth - CONTEXTUAL_COMPOSE_MARGIN) {
    left = anchor.left - width - CONTEXTUAL_COMPOSE_MARGIN;
  }
  if (left < CONTEXTUAL_COMPOSE_MARGIN) {
    left = Math.min(
      Math.max(CONTEXTUAL_COMPOSE_MARGIN, anchor.left),
      viewportWidth - width - CONTEXTUAL_COMPOSE_MARGIN,
    );
  }

  let top = anchor.top;
  if (top + CONTEXTUAL_COMPOSE_ESTIMATED_HEIGHT > viewportHeight - CONTEXTUAL_COMPOSE_MARGIN) {
    top = viewportHeight - CONTEXTUAL_COMPOSE_ESTIMATED_HEIGHT - CONTEXTUAL_COMPOSE_MARGIN;
  }
  if (top < CONTEXTUAL_COMPOSE_MARGIN) {
    top = CONTEXTUAL_COMPOSE_MARGIN;
  }

  return {
    position: "fixed",
    left: Math.round(left),
    top: Math.round(top),
    width: Math.round(width),
    zIndex: 70,
  };
}

function summarizeWorkspaceNodes(nodes: WorkspaceNode[]): {
  workstreams: number;
  openIssues: number;
} {
  let workstreams = 0;
  let openIssues = 0;

  const walk = (items: WorkspaceNode[]) => {
    for (const item of items) {
      if (item.type === "workstream") {
        workstreams++;
      }
      if (item.type === "issue") {
        const status = String(item.metadata?.status ?? "open").toLowerCase();
        if (!["resolved", "closed", "done", "archived"].includes(status)) {
          openIssues++;
        }
      }
      if (item.children?.length) {
        walk(item.children);
      }
    }
  };

  walk(nodes);
  return { workstreams, openIssues };
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function WorkspaceSurfaceLoading(): React.ReactElement {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex items-center gap-3 text-sm" style={{ color: "var(--c-text-secondary)" }}>
        <div
          className="h-4 w-4 animate-spin rounded-full border-2"
          style={{ borderColor: "var(--c-border)", borderTopColor: "var(--c-primary)" }}
        />
        Loading view...
      </div>
    </div>
  );
}

const SURFACE_COMPOSER_FEEDBACK_MS = 5500;

interface LifeCommandResponse {
  handled?: boolean;
  acknowledgment?: string;
}

function compactComposerFeedback(message: string): string {
  const normalized = message.replace(/\s+/g, " ").trim();
  if (normalized.length <= 180) return normalized;
  return `${normalized.slice(0, 177).trim()}...`;
}

export default function WorkspaceShell({
  sseConnected,
  notificationCount,
  sseEvents,
  theme,
  onThemeToggle,
  needsOnboarding = false,
  onboardingBlocker = null,
}: WorkspaceShellProps): React.ReactElement {
  const navigate = useNavigate();
  const paletteCommands = useMemo(() => buildPaletteCommands(navigate), [navigate]);
  const [panelCollapsed, setPanelCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT,
  );
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT,
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(false);
  const [activeApp, setActiveApp] = useState<AppData | null>(null);
  const [activeView, setActiveView] = useState<"chat" | "app" | "life" | "comms" | "files">("chat");
  const [globalComposerFeedback, setGlobalComposerFeedback] =
    useState<WorkspaceComposerFeedback | null>(null);
  const [sessionId, setSessionId] = useState(resolveInitialSessionId);
  const [agentName, setAgentName] = useState(DEFAULT_AGENT_NAME);
  const [initialConnectDone, setInitialConnectDone] = useState(false);
  const [activeSelectedTarget, setActiveSelectedTarget] = useState<SelectedTarget | null>(null);
  const [contextualComposeTarget, setContextualComposeTarget] = useState<{
    contextId: string;
    label: string;
    anchor: ElementAnchorRect;
    context: Record<string, unknown>;
  } | null>(null);
  const workspaceRootRef = useRef<HTMLDivElement | null>(null);
  const selectedUiElementRef = useRef<HTMLElement | null>(null);
  const globalComposerFeedbackTimer = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const { sendChatMessage } = useChatBridge();

  const showGlobalComposerFeedback = useCallback((feedback: WorkspaceComposerFeedback) => {
    if (globalComposerFeedbackTimer.current) {
      window.clearTimeout(globalComposerFeedbackTimer.current);
    }
    setGlobalComposerFeedback(feedback);
    globalComposerFeedbackTimer.current = window.setTimeout(() => {
      setGlobalComposerFeedback(null);
      globalComposerFeedbackTimer.current = null;
    }, SURFACE_COMPOSER_FEEDBACK_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (globalComposerFeedbackTimer.current) {
        window.clearTimeout(globalComposerFeedbackTimer.current);
      }
    };
  }, []);

  const openSettings = useCallback(
    (target?: { sectionId?: string; pageId?: string }) => {
      navigate(buildSettingsPath(target?.sectionId, target?.pageId));
    },
    [navigate],
  );

  // Track first successful SSE connection to avoid "Agent offline" flash on load
  useEffect(() => {
    if (sseConnected && !initialConnectDone) {
      setInitialConnectDone(true);
    }
  }, [sseConnected, initialConnectDone]);

  // Debounced offline flag — keeps the status badge in its previous state for
  // 500 ms when SSE drops, so brief reconnect blips do not flash "Offline".
  const [debouncedOffline, setDebouncedOffline] = useState(false);
  useEffect(() => {
    if (sseConnected) {
      setDebouncedOffline(false);
      return;
    }
    const timer = window.setTimeout(() => setDebouncedOffline(true), 500);
    return () => window.clearTimeout(timer);
  }, [sseConnected]);

  // Auto-collapse sidebar on narrow viewports
  useEffect(() => {
    const handleResize = () => {
      const narrow = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(narrow);
      if (narrow) setPanelCollapsed(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Workspace nodes — fetched from API, with SSE-driven updates
  const [workspaceNodes, setWorkspaceNodes] = useState<WorkspaceNode[]>(DEFAULT_WORKSPACE_NODES);
  const processedOrgEvents = useRef<Set<string>>(new Set());
  const processedAppEvents = useRef<Set<string>>(new Set());

  // Fetch workspace organization on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/workspace/organization");
        if (res.ok && !cancelled) {
          const data = await res.json();
          if (data.nodes && Array.isArray(data.nodes)) {
            setWorkspaceNodes(data.nodes);
          }
        }
      } catch {
        // Keep defaults on failure
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch persisted onboarding profile to hydrate display name.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/onboarding");
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          agentName?: unknown;
          answers?: { agentName?: unknown };
        };
        const configuredName =
          typeof data.answers?.agentName === "string" && data.answers.agentName.trim()
            ? data.answers.agentName
            : data.agentName;
        if (typeof configuredName === "string" && configuredName.trim()) {
          setAgentName(configuredName.trim().slice(0, 48));
        }
      } catch {
        // Keep default display name on failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for mock agent workspace updates (custom event from mock-data.ts)
  useEffect(() => {
    const handler = () => {
      fetch("/api/workspace/organization")
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.nodes && Array.isArray(data.nodes)) {
            setWorkspaceNodes(data.nodes);
          }
        })
        .catch(() => {});
    };
    window.addEventListener("mock-workspace-updated", handler);
    return () => window.removeEventListener("mock-workspace-updated", handler);
  }, []);

  // Process SSE events for workspace organization changes
  useEffect(() => {
    if (!sseEvents) return;
    for (const event of sseEvents) {
      const eventKey = `${event.event}-${event.timestamp}`;
      if (processedOrgEvents.current.has(eventKey)) continue;

      if (
        event.event === "workspace:organization_updated" ||
        event.event === "workspace:node_added" ||
        event.event === "workspace:node_updated" ||
        event.event === "workspace:node_removed" ||
        event.event === "workspace:node_moved"
      ) {
        processedOrgEvents.current.add(eventKey);
        fetch("/api/workspace/organization")
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data?.nodes && Array.isArray(data.nodes)) {
              setWorkspaceNodes(data.nodes);
            }
          })
          .catch(() => {});
      }
    }
  }, [sseEvents]);

  // Process SSE events for workspace app lifecycle changes (app_created, app_updated, app_removed)
  useEffect(() => {
    if (!sseEvents) return;
    handleAppSSEEvents({
      events: sseEvents,
      processedKeys: processedAppEvents.current,
      refetchApp: (appId) => {
        // Re-fetch and update the active app if it matches the updated/created app
        fetch(`/api/workspace/apps/${encodeURIComponent(appId)}`)
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data && data.html) {
              setActiveApp((prev) => {
                if (prev && prev.id === appId) {
                  return mapBackendAppToAppData(data, prev.title);
                }
                return prev;
              });
            }
          })
          .catch(() => {});
      },
      closeApp: (appId) => {
        setActiveApp((prev) => {
          if (prev && prev.id === appId) {
            setActiveView("chat");
            return null;
          }
          return prev;
        });
      },
    });
  }, [sseEvents]);

  // Pending approvals (from /api/approvals — sourced by approval-queue route,
  // which aggregates approval items + pending email drafts).
  const [pendingApprovals, setPendingApprovals] = useState<
    Array<{ id: string; type: string; title: string; priority?: string; requestedAt: string }>
  >([]);
  useEffect(() => {
    let cancelled = false;
    const loadApprovals = async () => {
      try {
        const res = await fetch("/api/approvals?status=pending");
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as {
          items?: Array<{
            id: string;
            type: string;
            title: string;
            priority?: string;
            requestedAt: string;
          }>;
        };
        if (!cancelled && Array.isArray(data.items)) {
          setPendingApprovals(data.items);
        }
      } catch {
        // Keep existing state on failure.
      }
    };
    void loadApprovals();
    return () => {
      cancelled = true;
    };
  }, []);

  const workspaceSummary = useMemo(() => summarizeWorkspaceNodes(workspaceNodes), [workspaceNodes]);
  const globalComposerSurface = useMemo(() => {
    if (activeView === "app" && activeApp?.id) {
      if (activeApp.id === "app-projects") return "projects";
      if (activeApp.id === "app-conversations") return "conversations";
      if (activeApp.id === "app-workstreams") return "workstreams";
      if (activeApp.id === "app-files") return "files";
      if (activeApp.id === "app-settings") return "settings";
      return "apps";
    }
    return activeView;
  }, [activeApp?.id, activeView]);
  const globalComposerContext = useMemo(
    () =>
      buildWorkspaceBridgeContext({
        surface: globalComposerSurface,
        activeApp,
        summary: workspaceSummary,
        selectedTarget: activeSelectedTarget,
      }),
    [activeApp, activeSelectedTarget, globalComposerSurface, workspaceSummary],
  );
  const contextualComposeStyle = useMemo(
    () =>
      contextualComposeTarget
        ? computeContextualComposeStyle(contextualComposeTarget.anchor)
        : null,
    [contextualComposeTarget],
  );

  const clearSelectedUiElement = useCallback(() => {
    if (selectedUiElementRef.current) {
      selectedUiElementRef.current.classList.remove("workspace-ui-selected");
      selectedUiElementRef.current = null;
    }
    setActiveSelectedTarget(null);
  }, []);

  const buildShellSelectedTarget = useCallback(
    (selectable: HTMLElement, explicitLabel?: string): SelectedTarget | undefined =>
      extractSelectedTargetFromElement(selectable, {
        label: explicitLabel ?? deriveElementLabel(selectable),
        surface: activeView === "app" ? `app:${activeApp?.id ?? "unknown"}` : activeView,
        selector: buildElementSelector(selectable),
        appId: activeApp?.id,
      }),
    [activeApp?.id, activeView],
  );

  const markUiElementSelected = useCallback(
    (element: HTMLElement | null) => {
      if (!element) {
        clearSelectedUiElement();
        return;
      }
      if (selectedUiElementRef.current === element) return;
      clearSelectedUiElement();
      element.classList.add("workspace-ui-selected");
      selectedUiElementRef.current = element;
    },
    [clearSelectedUiElement],
  );

  const shouldIgnoreGlobalSelection = useCallback((target: HTMLElement): boolean => {
    if (target.closest("[data-contextual-chat-compose='true']")) return true;
    if (target.closest(".work-panel__inline-chat")) return true;
    if (target.closest(".agent-dialog")) return true;
    if (target.closest("[data-node-id]")) return true;
    return isEditableTarget(target);
  }, []);

  const findSelectableUiElement = useCallback((target: HTMLElement): HTMLElement | null => {
    const root = workspaceRootRef.current;
    const lifeItem = target.closest("[data-life-item-id]");
    if (lifeItem instanceof HTMLElement) return lifeItem;

    let current: HTMLElement | null = target;

    while (current && current !== root) {
      if (current.dataset.contextualChatCompose === "true") return null;
      if (current.tagName === "SCRIPT" || current.tagName === "STYLE") {
        current = current.parentElement;
        continue;
      }

      const rect = current.getBoundingClientRect();
      const hasArea = rect.width >= 24 && rect.height >= 16;
      const notTooLarge =
        rect.width < window.innerWidth * 0.96 && rect.height < window.innerHeight * 0.88;
      const label = deriveElementLabel(current);

      if (hasArea && notTooLarge && label.length > 0) {
        return current;
      }
      current = current.parentElement;
    }

    return null;
  }, []);

  const openContextualCompose = useCallback(
    (label: string, context: Record<string, unknown>, anchor: ElementAnchorRect) => {
      const contextId = `ui-context-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      emitUxTelemetry("ux.contextual_feedback.started", {
        contextType: "workspace_element",
        contextId,
      });
      setContextualComposeTarget({
        contextId,
        label,
        context,
        anchor,
      });
    },
    [],
  );

  const handleWorkspaceClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (shouldIgnoreGlobalSelection(target)) return;

      const selectable = findSelectableUiElement(target);
      if (!selectable) {
        clearSelectedUiElement();
        return;
      }

      markUiElementSelected(selectable);
      setActiveSelectedTarget(buildShellSelectedTarget(selectable) ?? null);
    },
    [
      buildShellSelectedTarget,
      clearSelectedUiElement,
      findSelectableUiElement,
      markUiElementSelected,
      shouldIgnoreGlobalSelection,
    ],
  );

  const handleWorkspaceDoubleClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (event.defaultPrevented) return;
      if (shouldIgnoreGlobalSelection(target)) return;
      if (isExpansionIntentTarget(target)) return;
      const selection = window.getSelection();
      const selectionText = selection?.toString().trim();
      if (selectionText) {
        const anchor = selection?.anchorNode;
        const focus = selection?.focusNode;
        const withinTarget =
          Boolean(anchor && target.contains(anchor)) && Boolean(focus && target.contains(focus));
        if (!withinTarget) return;
        selection?.removeAllRanges();
      }

      const selectable = findSelectableUiElement(target);
      if (!selectable) return;

      markUiElementSelected(selectable);

      const label = deriveElementLabel(selectable);
      const rect = selectable.getBoundingClientRect();
      const selectedTarget = buildShellSelectedTarget(selectable, label);
      setActiveSelectedTarget(selectedTarget ?? null);

      openContextualCompose(
        label,
        {
          uiElementLabel: label,
          uiElementSelector: buildElementSelector(selectable),
          uiSurface: activeView === "app" ? `app:${activeApp?.id ?? "unknown"}` : activeView,
          ...(selectedTarget ? { selectedTarget } : {}),
          ...(activeApp ? { appId: activeApp.id } : {}),
        },
        {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        },
      );
    },
    [
      buildShellSelectedTarget,
      findSelectableUiElement,
      markUiElementSelected,
      openContextualCompose,
      shouldIgnoreGlobalSelection,
    ],
  );

  const requestContextualReply = useCallback(
    async (
      label: string,
      context: Record<string, unknown>,
      text: string,
    ): Promise<ContextualPaneResult> => {
      const userPrompt = [`Selected element: ${label}`, "", `User request: ${text}`].join("\n");

      const reply = await requestContextualChatReply({
        systemPrompt:
          "You are the user's agent in a contextual inline UI pane. Respond in 1-2 concise sentences. Stay specific to the selected element and ask one clarifying question when needed.",
        userPrompt,
        surface: "contextual_chat",
        contextSummary: label,
        uiContext: context,
        provenance: buildSelectedTargetProvenance(
          (context.selectedTarget as SelectedTarget | undefined) ?? undefined,
        ),
      });
      if (reply?.assistantReply) {
        return {
          assistantReply: reply.assistantReply,
          closeAfterSend: false,
        };
      }

      return {
        assistantReply:
          "Understood. I will work within this selected context. What should I prioritize first here?",
        closeAfterSend: false,
      };
    },
    [],
  );

  useEffect(() => {
    return () => clearSelectedUiElement();
  }, [clearSelectedUiElement]);

  useEffect(() => {
    clearSelectedUiElement();
  }, [activeApp?.id, activeView, clearSelectedUiElement]);

  useEffect(() => {
    if (sessionId) {
      saveLastActiveConversationId(sessionId);
    }
  }, [sessionId]);

  const handleNewChat = useCallback(() => {
    setSessionId(generateSessionId());
    setActiveApp(null);
    setActiveView("chat");
  }, []);

  const handleSwitchConversation = useCallback((newSessionId: string) => {
    setSessionId(newSessionId);
    setActiveApp(null);
    setActiveView("chat");
  }, []);

  // Open an app by ID (fetches HTML and renders in AppContainer)
  const openApp = useCallback((appId: string, fallbackTitle?: string) => {
    fetch(`/api/workspace/apps/${encodeURIComponent(appId)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data && data.html) {
          setActiveApp(mapBackendAppToAppData(data, fallbackTitle || appId));
          setActiveView("app");
        }
      })
      .catch(() => {});
  }, []);

  const openWorkspaceCollection = useCallback(
    (collectionId: WorkspaceCollectionId) => {
      setActiveApp(
        buildWorkspaceCollectionApp({
          collectionId,
          nodes: workspaceNodes,
          conversations: collectionId === "conversations" ? listSavedConversations() : [],
        }),
      );
      setActiveView("app");
    },
    [workspaceNodes],
  );

  const openWorkspaceNodeDetail = useCallback(
    (node: WorkspaceNode) => {
      const projectsRoot = workspaceNodes.find((n) => n.id === "projects");
      const workstreamsRoot = workspaceNodes.find((n) => n.id === "workstreams");
      const filesRoot = workspaceNodes.find((n) => n.id === "files");
      const conversationsRoot = workspaceNodes.find((n) => n.id === "conversations");

      const linkedProjects =
        node.type === "workstream"
          ? (projectsRoot?.children ?? []).filter(
              (project) => String(project.metadata?.workstreamId ?? "") === node.id,
            )
          : node.type === "project"
            ? [node]
            : [];

      const linkedFiles = (filesRoot?.children ?? []).filter((file) => {
        const projectId = String(file.metadata?.projectId ?? "");
        const workstreamId = String(file.metadata?.workstreamId ?? "");
        if (node.type === "project") return projectId === node.id;
        if (node.type === "workstream") return workstreamId === node.id;
        if (node.type === "issue")
          return workstreamId === String(node.metadata?.workstreamId ?? "");
        return false;
      });

      const linkedConversations = (conversationsRoot?.children ?? []).filter((conversation) => {
        const projectId = String(conversation.metadata?.projectId ?? "");
        const sessionId = String(conversation.metadata?.sessionId ?? "");
        if (node.type === "project") {
          const directSession = String(node.metadata?.sessionId ?? "");
          return projectId === node.id || (directSession !== "" && directSession === sessionId);
        }
        if (node.type === "workstream") {
          return linkedProjects.some((project) => project.id === projectId);
        }
        if (node.type === "issue") {
          const issueProjectId = String(node.metadata?.projectId ?? "");
          return issueProjectId !== "" && projectId === issueProjectId;
        }
        return false;
      });

      const workstreamNode =
        node.type === "workstream"
          ? node
          : node.type === "project"
            ? (workstreamsRoot?.children ?? []).find(
                (workstream) => workstream.id === String(node.metadata?.workstreamId ?? ""),
              )
            : null;
      const linkedIssues =
        workstreamNode?.children?.filter((child) => child.type === "issue").slice(0, 6) ?? [];

      const summaryRows: Array<{ label: string; value: string }> = [];
      if (node.type === "project") {
        summaryRows.push({
          label: "Workstream",
          value: workstreamNode?.label ?? String(node.metadata?.workstreamId ?? "Unassigned"),
        });
      }
      if (node.type === "workstream") {
        summaryRows.push({
          label: "Progress",
          value:
            typeof node.metadata?.progress === "number"
              ? `${Math.round(Number(node.metadata.progress))}%`
              : "Not set",
        });
      }
      if (node.type === "issue") {
        summaryRows.push({
          label: "Priority",
          value: String(node.metadata?.priority ?? "Normal"),
        });
        summaryRows.push({
          label: "Status",
          value: String(node.metadata?.status ?? "Open"),
        });
      }

      const summaryHtml = summaryRows
        .map(
          (row) =>
            `<div class="detail-summary-item"><div class="detail-summary-label">${escapeHtml(row.label)}</div><div class="detail-summary-value">${escapeHtml(row.value)}</div></div>`,
        )
        .join("");

      const projectHtml =
        linkedProjects.length > 0
          ? linkedProjects
              .map(
                (project) => `<article class="detail-card">
  <div class="detail-card-title">${escapeHtml(project.label)}</div>
  <div class="detail-card-meta">${escapeHtml(String(project.metadata?.status ?? "active"))}</div>
  <button class="detail-action" data-open-node="${escapeHtml(project.id)}">Open project</button>
</article>`,
              )
              .join("")
          : `<div class="detail-empty">No linked projects yet.</div>`;

      const issueHtml =
        linkedIssues.length > 0
          ? linkedIssues
              .map(
                (issue) => `<article class="detail-card">
  <div class="detail-card-title">${escapeHtml(issue.label)}</div>
  <div class="detail-card-meta">${escapeHtml(String(issue.metadata?.priority ?? "normal"))} priority · ${escapeHtml(String(issue.metadata?.status ?? "open"))}</div>
</article>`,
              )
              .join("")
          : `<div class="detail-empty">No linked issues yet.</div>`;

      const fileHtml =
        linkedFiles.length > 0
          ? linkedFiles
              .map((file) => {
                const fileSource =
                  String(file.metadata?.source ?? "agent").toLowerCase() === "user"
                    ? "You"
                    : "Agent";
                return `<article class="detail-card">
  <div class="detail-card-title">${escapeHtml(file.label)}</div>
  <div class="detail-card-meta">${escapeHtml(fileSource)} · ${escapeHtml(String(file.metadata?.fileSize ?? ""))}</div>
</article>`;
              })
              .join("")
          : `<div class="detail-empty">No files linked yet.</div>`;

      const conversationHtml =
        linkedConversations.length > 0
          ? linkedConversations
              .map((conversation) => {
                const session = String(conversation.metadata?.sessionId ?? "");
                const openButton =
                  session !== ""
                    ? `<button class="detail-action detail-action-primary" data-open-session="${escapeHtml(session)}">Open chat</button>`
                    : "";
                return `<article class="detail-card">
  <div class="detail-card-title">${escapeHtml(conversation.label)}</div>
  <div class="detail-card-meta">${escapeHtml(String(conversation.metadata?.projectLabel ?? ""))}</div>
  ${openButton}
</article>`;
              })
              .join("")
          : `<div class="detail-empty">No linked conversations yet.</div>`;

      const html = `<style>
  .detail-shell { padding: 16px; display: grid; gap: 14px; }
  .detail-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .detail-title { font-size: 20px; font-weight: 700; color: var(--c-text); line-height: 1.15; }
  .detail-kicker { font-size: 11px; color: var(--c-text-faint); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; }
  .detail-summary { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
  .detail-summary-item { border: 1px solid var(--c-border); border-radius: 10px; padding: 10px; background: var(--c-surface); }
  .detail-summary-label { font-size: 10px; color: var(--c-text-faint); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 4px; }
  .detail-summary-value { font-size: 13px; color: var(--c-text); font-weight: 600; }
  .detail-sections { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); }
  .detail-section { border: 1px solid var(--c-border); border-radius: 12px; background: var(--c-surface); overflow: hidden; min-height: 150px; display: flex; flex-direction: column; }
  .detail-section-head { padding: 10px 12px; border-bottom: 1px solid var(--c-border); font-size: 11px; color: var(--c-text-faint); text-transform: uppercase; letter-spacing: 0.08em; }
  .detail-section-body { padding: 10px; display: grid; gap: 8px; }
  .detail-card { border: 1px solid var(--c-border); border-radius: 10px; padding: 10px; background: var(--c-surface-2); }
  .detail-card-title { font-size: 13px; color: var(--c-text); font-weight: 600; margin-bottom: 4px; }
  .detail-card-meta { font-size: 11px; color: var(--c-text-faint); margin-bottom: 8px; }
  .detail-action { border: 1px solid var(--c-border); background: transparent; color: var(--c-text-secondary); border-radius: 8px; padding: 5px 8px; font-size: 11px; font-family: inherit; cursor: pointer; }
  .detail-action-primary { background: var(--c-accent); border-color: transparent; color: var(--c-accent-text); font-weight: 600; }
  .detail-empty { font-size: 12px; color: var(--c-text-faint); padding: 12px 6px; }
  @media (max-width: 720px) {
    .detail-shell { padding: 12px; gap: 10px; }
    .detail-sections { grid-template-columns: 1fr; }
  }
</style>
<div class="detail-shell">
  <header class="detail-header">
    <div>
      <div class="detail-kicker">${escapeHtml(node.type)}</div>
      <h1 class="detail-title">${escapeHtml(node.label)}</h1>
    </div>
  </header>
  ${summaryHtml ? `<section class="detail-summary">${summaryHtml}</section>` : ""}
  <section class="detail-sections">
    <section class="detail-section">
      <header class="detail-section-head">Linked Conversations</header>
      <div class="detail-section-body">${conversationHtml}</div>
    </section>
    <section class="detail-section">
      <header class="detail-section-head">Linked Files</header>
      <div class="detail-section-body">${fileHtml}</div>
    </section>
    <section class="detail-section">
      <header class="detail-section-head">Projects</header>
      <div class="detail-section-body">${projectHtml}</div>
    </section>
    <section class="detail-section">
      <header class="detail-section-head">Issues</header>
      <div class="detail-section-body">${issueHtml}</div>
    </section>
  </section>
</div>
<script>
  document.querySelectorAll('[data-open-session]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const sessionId = btn.getAttribute('data-open-session');
      if (sessionId && window.agentAction) {
        window.agentAction('navigate', { target: 'conversation', sessionId });
      }
    });
  });
  document.querySelectorAll('[data-open-node]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const nodeId = btn.getAttribute('data-open-node');
      if (nodeId && window.agentAction) {
        window.agentAction('navigate', { target: 'node', nodeId });
      }
    });
  });
</script>`;

      setActiveApp({
        id: `app-detail-${node.id}`,
        title: node.label,
        html,
        designSystem: true,
        sourceMode: "legacy_html",
      });
      setActiveView("app");
    },
    [workspaceNodes],
  );

  // Handle node clicks — navigate to conversation, open app, view file, etc.
  const handleNodeClick = useCallback(
    (node: WorkspaceNode) => {
      const destination = resolveWorkspaceNodeDestination(node);
      if (!destination) return;

      switch (destination.kind) {
        case "view":
          setActiveApp(null);
          setActiveView(destination.view);
          return;
        case "collection":
          openWorkspaceCollection(destination.collectionId);
          return;
        case "app":
          openApp(destination.appId, node.label);
          return;
        case "conversation":
          handleSwitchConversation(destination.sessionId);
          return;
        case "detail":
          openWorkspaceNodeDetail(node);
          return;
      }
    },
    [handleSwitchConversation, openApp, openWorkspaceCollection, openWorkspaceNodeDetail],
  );

  const openWorkspaceSurface = useCallback(
    (surface: WorkspaceTourSurface | "settings") => {
      if (surface === "settings") {
        openSettings();
        return;
      }
      const node = workspaceNodes.find((entry) => entry.id === surface);
      if (node) {
        handleNodeClick(node);
      }
    },
    [handleNodeClick, openSettings, workspaceNodes],
  );

  const findWorkspaceNodeById = useCallback(
    (nodeId: string): WorkspaceNode | null => {
      const stack = [...workspaceNodes];
      while (stack.length > 0) {
        const current = stack.shift();
        if (!current) continue;
        if (current.id === nodeId) return current;
        if (current.children?.length) stack.push(...current.children);
      }
      return null;
    },
    [workspaceNodes],
  );

  const handleAppAction = useCallback(
    (action: AppAction) => {
      if (action.type === "close") {
        setActiveApp(null);
        setActiveView("chat");
        return;
      }

      if (action.type === "navigate") {
        const payload = (action.payload ?? {}) as Record<string, unknown>;
        const target = typeof payload.target === "string" ? payload.target : "";
        if (target === "conversation") {
          const targetSessionId =
            typeof payload.sessionId === "string" ? String(payload.sessionId) : null;
          if (targetSessionId) {
            handleSwitchConversation(targetSessionId);
            return;
          }
        }
        if (target === "project" || target === "node") {
          const nodeId = typeof payload.nodeId === "string" ? String(payload.nodeId) : null;
          if (nodeId) {
            const node = findWorkspaceNodeById(nodeId);
            if (node) {
              handleNodeClick(node);
              return;
            }
          }
        }
      }

      if (action.type === "custom") {
        const payload = (action.payload ?? {}) as Record<string, unknown>;
        const kind = typeof payload.kind === "string" ? payload.kind : "";

        if (kind === "contextual_chat_open") {
          const label =
            typeof payload.label === "string" && payload.label.trim()
              ? payload.label.trim()
              : "Selected app element";
          const viewportX =
            typeof payload.viewportX === "number"
              ? Number(payload.viewportX)
              : window.innerWidth - 32;
          const viewportY =
            typeof payload.viewportY === "number"
              ? Number(payload.viewportY)
              : window.innerHeight - 32;
          const targetWidth =
            typeof payload.targetWidth === "number" ? Number(payload.targetWidth) : 24;
          const targetHeight =
            typeof payload.targetHeight === "number" ? Number(payload.targetHeight) : 24;

          const selectedTarget = {
            label,
            selector: typeof payload.selector === "string" ? payload.selector : "",
            surface: `app:${activeApp?.id ?? "unknown"}`,
            objectId: typeof payload.objectId === "string" ? payload.objectId : undefined,
            objectKind: typeof payload.objectKind === "string" ? payload.objectKind : undefined,
            fileId: typeof payload.fileId === "string" ? payload.fileId : undefined,
            actionId: typeof payload.actionId === "string" ? payload.actionId : undefined,
            messageId: typeof payload.messageId === "string" ? payload.messageId : undefined,
            conversationId:
              typeof payload.conversationId === "string" ? payload.conversationId : undefined,
            resourceUri: typeof payload.resourceUri === "string" ? payload.resourceUri : undefined,
            appId: activeApp?.id,
          };
          setActiveSelectedTarget(selectedTarget);

          openContextualCompose(
            label,
            {
              uiElementLabel: label,
              uiElementSelector: typeof payload.selector === "string" ? payload.selector : "",
              uiSurface: `app:${activeApp?.id ?? "unknown"}`,
              selectedTarget,
              ...(activeApp ? { appId: activeApp.id } : {}),
            },
            {
              left: viewportX,
              top: viewportY,
              width: targetWidth,
              height: targetHeight,
            },
          );
          return;
        }

        if (kind === "new_conversation") {
          handleNewChat();
          return;
        }
      }

      console.log("App action:", action);
    },
    [
      activeApp,
      findWorkspaceNodeById,
      handleNodeClick,
      handleNewChat,
      handleSwitchConversation,
      openContextualCompose,
    ],
  );

  const handleLifeSurfaceComposerSend = useCallback(
    async (text: string, context: Record<string, unknown>) => {
      try {
        const selectedTarget =
          context.selectedTarget && typeof context.selectedTarget === "object"
            ? (context.selectedTarget as SelectedTarget)
            : undefined;
        const res = await fetch("/api/life/canvas/command", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text,
            context,
            metadata: {
              surface: "life",
              uiContext: context,
              provenance: buildSelectedTargetProvenance(selectedTarget),
            },
          }),
        });
        if (res.ok) {
          let data: LifeCommandResponse = {};
          try {
            data = (await res.json()) as LifeCommandResponse;
          } catch {
            data = {};
          }
          const acknowledgment =
            typeof data.acknowledgment === "string" && data.acknowledgment.trim()
              ? data.acknowledgment
              : "Updated your Life canvas.";
          showGlobalComposerFeedback({
            tone: data.handled === false ? "info" : "success",
            message: compactComposerFeedback(acknowledgment),
          });
          window.dispatchEvent(new CustomEvent("life-canvas:refresh"));
          return;
        }
      } catch {
        // Fall through to chat so unsupported Life commands still get an answer.
      }

      sendChatMessage(text, {
        ...context,
      });
      setActiveView("chat");
    },
    [sendChatMessage, showGlobalComposerFeedback],
  );

  // Track V2c — DragDropProvider wiring DEFERRED to a separate follow-up.
  //
  // The original V2c plan called for mounting DragDropProvider at the
  // WorkspaceShell so descendant blocks could rely on
  // `useDragDrop().hasProvider`. BugBot caught a regression on PR #480:
  // when KanbanBlock detected the ambient provider it skipped mounting
  // its own DragDropContext, but the host-level handler had no
  // onMove dispatch back to KanbanBlock — so drag-drop visually worked
  // but never persisted. Reverting the wiring here keeps KanbanBlock's
  // standalone DragDropContext as the single source of truth until a
  // proper ambient↔block onMove protocol lands.
  //
  // Follow-up scope (separate PR): design an ambient handler that
  // either (a) dispatches a typed event each block can subscribe to and
  // call its own onMove, or (b) supplies a callback registry blocks
  // register into during mount. Either path needs explicit tests for
  // the persistence path, which the original wiring lacked.

  return (
    <AgentPresenceProvider>
      <LifeSelectionProvider>
        <div
          ref={workspaceRootRef}
          className="flex flex-col h-screen"
          style={{ backgroundColor: "var(--c-bg)", color: "var(--c-text)" }}
          onClickCapture={handleWorkspaceClickCapture}
          onDoubleClickCapture={handleWorkspaceDoubleClickCapture}
        >
          {/* Top bar */}
          <TopBar
            agentName={agentName}
            notificationCount={notificationCount}
            sseConnected={sseConnected}
            onSettingsClick={() => openSettings()}
            onSidebarToggle={() => {
              if (isMobile) {
                setMobileMenuOpen(!mobileMenuOpen);
              } else {
                setPanelCollapsed(!panelCollapsed);
              }
            }}
            sidebarCollapsed={isMobile ? !mobileMenuOpen : panelCollapsed}
            theme={theme}
            onThemeToggle={onThemeToggle}
            isMobile={isMobile}
          />

          {/* Main content area: WorkPanel + ChatSpine or AppContainer */}
          <div className="flex flex-1 min-h-0 relative">
            {/* Mobile: icon-strip sidebar is always visible inline; expanded sidebar overlays when opened. */}
            {isMobile && mobileMenuOpen && (
              <div
                className="absolute inset-0 z-30"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
                onClick={() => setMobileMenuOpen(false)}
              />
            )}
            {/* Inline collapsed icon strip on mobile (always), or full sidebar on desktop. */}
            {(!isMobile || !mobileMenuOpen) && (
              <WorkPanel
                nodes={workspaceNodes}
                collapsed={isMobile ? true : panelCollapsed}
                onNodeClick={(node) => {
                  handleNodeClick(node);
                  if (isMobile) setMobileMenuOpen(false);
                }}
                onNodeSelect={(node) => {
                  setActiveSelectedTarget(
                    buildSelectedTargetFromWorkspaceNode(node, "sidebar") ?? null,
                  );
                }}
                onExpand={() => {
                  if (isMobile) setMobileMenuOpen(true);
                  else setPanelCollapsed(false);
                }}
                onSettingsClick={() => {
                  openSettings();
                  if (isMobile) setMobileMenuOpen(false);
                }}
                onNewConversation={() => {
                  handleNewChat();
                  if (isMobile) setMobileMenuOpen(false);
                }}
              />
            )}
            {/* Mobile: expanded sidebar overlay */}
            {isMobile && mobileMenuOpen && (
              <div className="absolute inset-y-0 left-0 z-40">
                <WorkPanel
                  nodes={workspaceNodes}
                  collapsed={false}
                  onNodeClick={(node) => {
                    handleNodeClick(node);
                    setMobileMenuOpen(false);
                  }}
                  onNodeSelect={(node) => {
                    setActiveSelectedTarget(
                      buildSelectedTargetFromWorkspaceNode(node, "sidebar") ?? null,
                    );
                  }}
                  onExpand={() => {
                    setMobileMenuOpen(true);
                  }}
                  onSettingsClick={() => {
                    openSettings();
                    setMobileMenuOpen(false);
                  }}
                  onNewConversation={() => {
                    handleNewChat();
                    setMobileMenuOpen(false);
                  }}
                />
              </div>
            )}

            {/* Main content: life view, app view, or chat spine */}
            <div
              className="flex-1 min-w-0 relative"
              style={{
                backgroundColor: "var(--c-bg)",
                paddingBottom: activeView !== "chat" ? (isMobile ? 108 : 120) : 0,
              }}
            >
              {activeView === "life" ? (
                <Suspense fallback={<WorkspaceSurfaceLoading />}>
                  <LazyLifeCanvas sseEvents={sseEvents} onClose={() => setActiveView("chat")} />
                </Suspense>
              ) : activeView === "comms" ? (
                <Suspense fallback={<WorkspaceSurfaceLoading />}>
                  <LazyCommsHub onClose={() => setActiveView("chat")} />
                </Suspense>
              ) : activeView === "files" ? (
                <Suspense fallback={<WorkspaceSurfaceLoading />}>
                  <div className="h-full overflow-y-auto">
                    <LazyContextFiles />
                  </div>
                </Suspense>
              ) : activeView === "app" && activeApp ? (
                <AppContainer
                  app={activeApp}
                  theme={theme}
                  onClose={() => {
                    setActiveApp(null);
                    setActiveView("chat");
                  }}
                  onAction={handleAppAction}
                />
              ) : (
                <ChatSpine
                  sessionId={sessionId}
                  sseEvents={sseEvents}
                  needsOnboarding={needsOnboarding && !onboardingDone}
                  onboardingBlocker={onboardingBlocker}
                  onOnboardingComplete={() => setOnboardingDone(true)}
                  onAgentNameChange={setAgentName}
                  agentName={agentName}
                  onNewChat={handleNewChat}
                  onSwitchConversation={handleSwitchConversation}
                  onOpenSettings={(target) => openSettings(target)}
                  onOpenWorkspaceSurface={openWorkspaceSurface}
                  onOpenWorkspaceApp={openApp}
                />
              )}
            </div>

            {activeView !== "chat" && (
              <div
                className="pointer-events-none absolute right-0 z-20 flex justify-center px-2 md:px-4"
                style={{
                  left: isMobile ? 56 : panelCollapsed ? 56 : 260,
                  bottom: isMobile ? 18 : 34,
                }}
              >
                <div className="pointer-events-auto w-full" style={{ maxWidth: 860 }}>
                  <WorkspaceComposer
                    surface={globalComposerSurface}
                    context={globalComposerContext}
                    feedback={globalComposerSurface === "life" ? globalComposerFeedback : null}
                    onSend={(text, context) => {
                      if (globalComposerSurface === "life") {
                        void handleLifeSurfaceComposerSend(text, context);
                        return;
                      }
                      sendChatMessage(text, {
                        ...context,
                      });
                      setActiveView("chat");
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Status bar */}
          <StatusBar
            agentStatus={
              sseConnected
                ? "active"
                : initialConnectDone && debouncedOffline
                  ? "offline"
                  : "connecting"
            }
            runningTasks={0}
            sseConnected={sseConnected}
            activeWorkstreams={workspaceSummary.workstreams}
            openIssues={workspaceSummary.openIssues}
            pendingApprovals={pendingApprovals.length}
          />

          {contextualComposeTarget && contextualComposeStyle && (
            <div style={contextualComposeStyle} data-contextual-chat-compose="true">
              <InlineChatCompose
                label={contextualComposeTarget.label}
                contextId={contextualComposeTarget.contextId}
                contextType="sidebar"
                onSend={async (text) => {
                  sendChatMessage(text, contextualComposeTarget.context);
                  const result = await requestContextualReply(
                    contextualComposeTarget.label,
                    contextualComposeTarget.context,
                    text,
                  );
                  emitUxTelemetry("ux.contextual_feedback.completed", {
                    contextType: "workspace_element",
                    contextId: contextualComposeTarget.contextId,
                  });
                  return result;
                }}
                onClose={() => setContextualComposeTarget(null)}
              />
            </div>
          )}

          {/* Cmd+K command palette — global navigation. Stateless w.r.t.
              shell state; the palette manages its own open/closed via
              the global keydown listener. */}
          <CommandPalette commands={paletteCommands} />

          {/* Agent presence overlay (workspace-wide whispers, toasts, dialogs, etc.) */}
          <AgentPresenceLayer />
        </div>
      </LifeSelectionProvider>
    </AgentPresenceProvider>
  );
}
